'use client';
import NavDropdownWrapper from './_NavDropdownWrapper';
import { useNavigationContext } from './index';
import { Anchor } from '@repo/ui/src/components/Anchor';
import ChevronIcon from '@repo/ui/src/components/Icons/ChevronIcon';
import { MetaProps } from '@repo/ui/src/types';
import React, { useState } from 'react';
import NavDropdown, { NavDropdownProps } from './_NavDropdown';
import { classNames } from '@repo/ui/src/utils/classNames';
import { LinkProps } from '@repo/ui/src/types/link';
import { navigationClicked } from '@repo/ui/src/services/analytics';

export interface SecondaryNavProps {
  _meta: MetaProps;
  items: Array<NavDropdownProps | LinkProps>;
  open: boolean;
  primaryHeader?: string;
  useKeyboardNavigation?: (options: any) => (e: React.KeyboardEvent) => void;
}

export const isNavDropdown = (
  navItem: NavDropdownProps | LinkProps,
): navItem is NavDropdownProps => {
  return navItem._meta?.schema === 'https://traeger.com/v2/navDropdown.json';
};

const SecondaryNav = ({ items = [], open, primaryHeader, useKeyboardNavigation }: SecondaryNavProps) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [openDropdown, setOpenDropdown] = useState<number | null>(0);
  const { setHeight } = useNavigationContext();
  const resetDropdown = () => setOpenDropdown(0);

  const handleKeyDown = useKeyboardNavigation?.({
    items,
    onChange: (index: number) => {
      if (isNavDropdown(items[index])) {
        setOpenDropdown(index);
      } else {
        setOpenDropdown(null);
        setHeight(0);
      }
      (
        ref.current?.querySelector(`[data-testid="secondary-nav-${index}"]`) as HTMLElement
      )?.focus();
    },
  });

  if (!items.length) return null;

  return (
    <div role="region" aria-label={`${primaryHeader} submenu`} ref={ref}>
      <ul
        className={classNames(
          'tw-list-none tw-grid tw-gap-x-8 sm:tw-flex sm:tw-justify-center sm:tw-gap-x-8 sm:tw-flex-wrap',
        )}
      >
        {items?.map((item, i) => {
          const itemOpen = openDropdown === i;
          const hasDropdown = isNavDropdown(item);
          const id = `secondary-nav-${i}`;
          return (
            <li key={i} className="tw-px-4 sm:tw-px-0">
              {!hasDropdown ? (
                <Anchor
                  {...item}
                  onKeyDown={handleKeyDown}
                  dataTestId={id}
                  data-index={i}
                  onClick={() => {
                    setOpenDropdown(null);
                    setHeight(0);
                    navigationClicked({
                      nav_type: 'Top Navigation',
                      clicked_text: item.displayName ?? '',
                      primary_header: primaryHeader ?? '',
                    });
                  }}
                  className={classNames(
                    'tw-inline-block tra-text-web-body-l hover:tw-text-brand-400 hover:tw-underline tw-transition-all tw-duration-300 tw-py-2',
                    itemOpen && 'tw-text-brand-400 tw-underline',
                  )}
                />
              ) : (
                <button
                  onClick={() => {
                    setOpenDropdown(i);
                    navigationClicked({
                      nav_type: 'Top Navigation',
                      clicked_text: item.title ?? '',
                      primary_header: primaryHeader ?? '',
                      secondary_header: item.title ?? '',
                    });
                  }}
                  onKeyDown={handleKeyDown}
                  className={classNames(
                    'tra-text-web-body-l tw-flex tw-justify-between tw-w-full sm:tw-w-auto sm:tw-block hover:tw-text-brand-400 hover:tw-underline tw-transition-all tw-duration-300 tw-py-2',
                    itemOpen && 'tw-text-brand-400 tw-underline',
                  )}
                  aria-expanded={itemOpen ? 'true' : 'false'}
                  aria-haspopup={hasDropdown ? 'true' : 'false'}
                  aria-controls={id}
                  data-index={i}
                  data-testid={id}
                >
                  {item.title}
                  <span className="tw-w-6 tw-inline-block sm:tw-hidden" aria-hidden="true">
                    <ChevronIcon direction="right" />
                  </span>
                </button>
              )}
              {hasDropdown && (
                <NavDropdownWrapper
                  id={id}
                  open={open && openDropdown === i && !!items?.length}
                  isDropdown={isNavDropdown(item)}
                  onTransitionEnd={() => {
                    if (!open) {
                      resetDropdown();
                    }
                  }}
                >
                  <NavDropdown
                    {...item}
                    primaryHeader={primaryHeader}
                    secondaryHeader={item?.title}
                  />
                </NavDropdownWrapper>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default SecondaryNav;
